import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors } from '../../../styles/colors';
import { Wizard } from '../Wizard';
import { QuestionnaireCustomerSelection } from './static-pages/QuestionnaireCustomerSelection';
import { Flexbox, FlexboxVertical } from '../Layout';
import { Button } from '../Button';
import { Customer, getAddressDisplayName } from '../../../interfaces/Customer';
import { Headline2, Headline6 } from '../Text';
import { QuestionnaireBaseQuestions } from './static-pages/QuestionnaireBaseQuestions';
import { NotesPopup } from './notes-popup/NotesPopup';
import { ProductCategoryList } from '../products/ProductCategoryList';
import { ProductDetailPage } from '../products/product-detail/ProductDetailPage';
import { QuestionnaireLeadQuestions } from './static-pages/QuestionnaireLeadQuestions';
import {
  ConcludingNotes,
  NotesSummary,
  QuestionnaireConcludingQuestions,
} from './static-pages/QuestionnaireConcludingQuestions';
import { useHistory } from 'react-router';
import {
  receiveResultSets, validateCondition,
  validateQuestionnaire,
} from './QuestionnaireFormBuilder';
import {
  Operation,
  OperationHistoryAddCustomer, OperationHistoryAddLead,
  OperationHistoryAnswerQuestion,
  OperationHistoryItem,
  OperationHistoryPreselectQuestion,
  OperationHistorySkipProductSelection,
  OperationHistoryUpdateCustomer,
  OperationStatus,
} from '../../../interfaces/Operation';
import * as AuthStore from '../../store/AuthStore';
import { Lead } from '../../../interfaces/Lead';
import { ApiDispatchItem, useApiDispatchQueue } from '../../../state/ApiDispatchState';
import { QuestionnaireContract } from './static-pages/QuestionnaireContract';
import { QuestionnaireSampleContract } from './static-pages/QuestionnaireSampleContract';
import { v4 as uuidv4 } from 'uuid';
import { CTAButtonProps } from '../products/product-detail/ProductDetailSummary';
import { SimpleAccordion } from '../SimpleAccordion';
import { QuestionnaireContext } from './QuestionnaireContextProvider';
import { NoDataPlaceholder } from '../NoDataPlaceholder';
import { QuestionnaireProductInfo } from './static-pages/QuestionnaireProductInfo';
import { useShowAlertPopup } from '../../../state/hooks/AlertHooks';

const ContentWrapper = styled(FlexboxVertical)`
  padding: 3.5rem 6rem;
`;

const ButtonWrapper = styled(Flexbox)`
  margin-top: 2.5rem;
  
  & > *:last-child {
    margin-left: auto;
  }
  
  & > *:nth-child(2) {
    margin-left: auto;
  }
 
  & > *:nth-child(3) {
    margin-left: 1rem;
  }
`;

const Headline = styled(Headline2)`
  color: ${Colors.primary};
  margin-bottom: .5rem;
`;

const AdditionalAddressesResultsWrapper = styled(FlexboxVertical)`
  margin: 1.5rem 0 0;
`;

interface Props {
  customers: Customer[] | null;
}

export const QuestionnairePage: FC<Props> = ({ ...props }) => {
  const history = useHistory();
  const dispatchApiCall = useApiDispatchQueue();
  const showNotificationBanner = useShowAlertPopup();
  const questionnaireState = useContext(QuestionnaireContext);
  const {
    questionnairePageVisible,

    currentPage, setCurrentPage,
    showErrorStates, setShowErrorStates,
    selectedCustomerIdRef,
    selectedCustomerState,
    openProductCategories, setOpenProductCategories,
    selectedProduct, setSelectedProduct,
    selectedProductAttachments,
    concludingNotes, setConcludingNotes,
    skipProductSelection,

    hasLeadPermission,
    hasSampleContractPermission,
    hasContractPermission,

    baseQuestionsState,
    baseQuestionsAnswered, setBaseQuestionsAnswered,
    baseQuestionsResultSets, setBaseQuestionsResultSets,
    additionalQuestionsState,
    additionalQuestionsAnswered, setAdditionalQuestionsAnswered,
    additionalQuestionsResultSets, setAdditionalQuestionsResultSets,
    openAccordions, setOpenAccordions,
    openProductCategoriesPerAddress, setOpenProductCategoriesPerAddress,
    additionalAddressSelected,

    leadQuestionsState,
    leadQuestionsAnswered, setLeadQuestionsAnswered,
    selectedUserLeadState,
    notes,
    lead, setLead,
    leadPageType,

    concludingQuestionsState,
    concludingQuestionsAnswered, setConcludingQuestionsAnswered,

    operation, setOperation,
    tmpOperation, setTmpOperation,
    getDefaultOperation,
    logAction,

    wizardData,
    customer,
    questionnaireBase,
    allQuestionnairesLeads,
    questionnaireConcludingQuestions,

    productsList,
    productsListPerAddress,

    onDocumentActionListener,
    loadStateFromOperation,
  } = questionnaireState;
  const [resultSetString, setResultSetString] = useState<string>('');

  const scrollToTop = (smooth: boolean = false) => {
    // scroll to top on page change
    if (smooth) {
      document.querySelector('main.app')?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    } else {
      document.querySelector('main.app')?.scrollTo(0, 0);
    }
  };
  useEffect(() => scrollToTop(), [currentPage]);

  const isBackButtonVisible = () => {
    switch (currentPage) {
      case 0:
        return false;
      case 3:       // product detail
        return false;
      default:
        return true;
    }
  };
  const isBackButtonEnabled = () => {
    return true;
  };
  const goBack = (count = 1) => setCurrentPage(Math.max(0, currentPage - count));
  const onBackButtonClick = () => {
    switch (currentPage) {
      case 0:
        break;
      case 4:
        resetLeadPage();    // reset lead
        break;
      case 5:
        // skip leads if user has no permissions
        if (skipProductSelection.current || !hasLeadPermission) {
          goBack(2);
          return;
        }
        break;
    }

    goBack();
  };

  const resetLeadPage = () => {
    leadQuestionsState[1]({});
    setLeadQuestionsAnswered([]);
    selectedUserLeadState[1](null);
    setLead(null);
    leadPageType.current = 'LEAD';
  };
  const onBackToResultsButtonClick = () => {
    resetLeadPage();    // reset lead
    additionalAddressSelected.current = null;   // reset additional address selection
    goBack(3);
  };

  const isNextButtonVisible = () => {
    switch (currentPage) {
      case 2:       // products selection
        return false;
      case 3:       // product detail
        return false;
      default:
        return true;
    }
  };
  const isNextButtonEnabled = () => {
    switch (currentPage) {
      case 0:
        return Boolean(selectedCustomerState[0]);
      default:
        return true;
    }
  };
  const goNext = (count = 1) => setCurrentPage(Math.min(5, currentPage + count));
  const onNextButtonClick = () => {
    // prepare nextOperation object
    let nextOperation = {
      ...(tmpOperation || getDefaultOperation()),
      questions: Object.keys(baseQuestionsState[0]).map((questionId) => {
        const answer = baseQuestionsState[0][questionId];
        const question = questionnaireBase?.questionnaire?.questions?.find(({ id }) => id === questionId);
        const conditions = question?.conditions || [];
        const visible = conditions.length < 1 || conditions.some((condition) => (
          validateCondition(baseQuestionsState[0], condition)
        ));
        if (visible) {
          return {
            id: questionId,
            label: question?.label || '',
            type: question?.type || '',
            required: question?.required || false,
            value: answer,
            answered: baseQuestionsAnswered.includes(questionId),
          };
        }
        return null;
      }).filter(Boolean),
      additionalQuestions: Object.keys(additionalQuestionsState[0]).reduce((res, key) => ({
        ...res,
        [key]: (
          Object.keys(additionalQuestionsState[0][key] || {}).map((questionId) => {
            const answer = additionalQuestionsState[0][key][questionId];
            const question = questionnaireBase?.questionnaire?.additionalQuestions?.find(({ id }) => id === questionId);
            const conditions = question?.conditions || [];
            const visible = conditions.length < 1 || conditions.some((condition) => (
              validateCondition(baseQuestionsState[0], condition)
            ));
            if (visible) {
              return {
                id: questionId,
                label: question?.label || '',
                type: question?.type || '',
                required: question?.required || false,
                value: answer,
                answered: (additionalQuestionsAnswered[key] || []).includes(questionId),
              };
            }
            return null;
          }).filter(Boolean)
        ),
      }), {}),
      concludingQuestions: Object.keys(concludingQuestionsState[0]).map((questionId) => {
        const answer = concludingQuestionsState[0][questionId];
        const question = questionnaireConcludingQuestions?.questionnaire?.questions?.find(({ id }) => id === questionId);
        const conditions = question?.conditions || [];
        const visible = conditions.length < 1 || conditions.some((condition) => (
          validateCondition(baseQuestionsState[0], condition)
        ));
        if (visible) {
          return {
            id: questionId,
            label: question?.label || '',
            type: question?.type || '',
            required: question?.required || false,
            value: answer,
            answered: concludingQuestionsAnswered.includes(questionId),
          };
        }
        return null;
      }).filter(Boolean),
      notes: notes?.list || [],
    } as Operation;

    // add concluding notes
    if (concludingNotes?.trim().length > 0) {
      nextOperation.notes = [...nextOperation.notes.filter(({ subject }) => subject !== 'Zusätzliche Anmerkungen'), {
        subject: 'Zusätzliche Anmerkungen',
        body: concludingNotes,
        timestamp: new Date().toISOString(),
      }];
    }

    switch (currentPage) {
      case 0:
        break;
      case 1:
        const baseAnswers = baseQuestionsState[0];
        const basePageValid = validateQuestionnaire(questionnaireBase, baseAnswers);
        console.log('base page valid', basePageValid);
        if (!basePageValid) {
          setShowErrorStates(true);
          return;
        }

        let foundResultSets = [];

        const baseQuestionsResultSets = receiveResultSets(questionnaireBase, baseAnswers);
        console.log('result sets', baseQuestionsResultSets);
        foundResultSets.push(...baseQuestionsResultSets);

        // result sets for additional addresses
        const additionalAddresses = customer?.additionalAddresses || [];
        const additionalResultSets = additionalAddresses.map((address, index) => {
          const key = `${index}`;
          const additionalAnswers = additionalQuestionsState[0][key] || {};
          const resultSets = receiveResultSets(questionnaireBase, { ...baseAnswers, ...additionalAnswers });

          console.log('result set for', address.street, resultSets);
          foundResultSets.push(...resultSets);

          setAdditionalQuestionsResultSets((prev) => ({
            ...prev,
            [key]: resultSets,
          }));
        });

        setBaseQuestionsResultSets(baseQuestionsResultSets);
        setResultSetString(foundResultSets.join(', '));

        // set operation status
        nextOperation.status = OperationStatus.OPEN;
        break;
      case 4:
        if (leadPageType.current === 'CONTRACT' && hasContractPermission) {
          //
        } else if (leadPageType.current === 'SAMPLE_CONTRACT' && hasSampleContractPermission) {
          //
        } else if (leadPageType.current === 'LEAD' && hasLeadPermission) {
          const leadPageValid = (
            (allQuestionnairesLeads || []).length < 1 ||
            (allQuestionnairesLeads || []).every((questionnaireLeads) => validateQuestionnaire(questionnaireLeads, ((leadQuestionsState[0] || {})[questionnaireLeads.id] || {})))
          ) && Boolean(selectedUserLeadState[0]);
          console.log('lead page valid', leadPageValid);
          if (!leadPageValid) {
            setShowErrorStates(true);
            return;
          }

          // add lead
          const dependencyId = `dependency_${uuidv4()}`;
          const nextLead = {
            id: dependencyId,
            createdAt: new Date().toISOString(),
            senderId: AuthStore.getCurrentUserId(),
            receiverId: selectedUserLeadState[0]?.id || '',
            customerId: selectedCustomerState[0]?.id || '',
            productId: selectedProduct?.id || '',
            questions: Object.keys(leadQuestionsState[0] || {}).flatMap((questionnaireLeadsId) => {
              const questionnaireLeads = allQuestionnairesLeads.find(({ id }) => id === questionnaireLeadsId);
              return Object.keys((leadQuestionsState[0] || {})[questionnaireLeadsId]).map((questionId) => {
                const answer = ((leadQuestionsState[0] || {})[questionnaireLeadsId] || {})[questionId];
                const question = questionnaireLeads?.questionnaire?.questions?.find(({ id }) => id === questionId);
                const conditions = question?.conditions || [];
                const visible = conditions.length < 1 || conditions.some((condition) => (
                  validateCondition(baseQuestionsState[0], condition)
                ));
                if (visible) {
                  return {
                    id: questionId,
                    label: question?.label || '',
                    type: question?.type || '',
                    required: question?.required || false,
                    value: answer,
                    answered: leadQuestionsAnswered.includes(questionId),
                  };
                }
                return null;
              }).filter(Boolean);
            }),
            notes: notes?.list || [],
          } as Partial<Lead>;

          // log lead action
          nextOperation = logAction({
            type: lead ? 'UPDATE_LEAD' : 'ADD_LEAD',
            lead: nextLead,
          } as OperationHistoryAddLead, nextOperation);

          // dispatch api call
          dispatchApiCall({
            method: lead ? 'put' : 'post',
            route: `/api/leads/${lead ? `${lead.id}/update` : 'create'}`,
            body: nextLead,
            header: AuthStore.getAuthHeader(),
            onSuccess: (res) => {
              if (!questionnairePageVisible.current) {
                return;
              }
              // console.log('lead response', res.data);
              setLead(res.data);
              // update history lead id
              setTmpOperation((tmp) => ({
                ...tmp,
                history: (tmp?.history || []).map((item) => {
                  if (item.type === 'ADD_LEAD' && (item as OperationHistoryAddLead).lead?.id === dependencyId) {
                    return {
                      ...item,
                      lead: res.data,   // lead data from api
                    };
                  }
                  return item;
                }),
              }));
            },
          } as ApiDispatchItem);
        }
        break;
      case 5:
        const concludingPageValid = validateQuestionnaire(questionnaireConcludingQuestions, concludingQuestionsState[0]);
        console.log('concluding questions page valid', concludingPageValid);

        if (concludingPageValid) {
          // set operation status
          nextOperation.status = OperationStatus.DONE;
        } else {
          setShowErrorStates(true);
          return;
        }
        break;
    }

    // log operation update
    nextOperation = logAction({
      type: nextOperation.status === OperationStatus.DONE ? 'COMPLETE_OPERATION' : 'UPDATE_OPERATION',
    } as OperationHistoryItem, nextOperation);

    // update operation
    // console.log('next operation', nextOperation, baseQuestionsState[0], notes?.list);
    // console.log('next operation', JSON.stringify(nextOperation));
    // console.log("current operation", operation, JSON.stringify(operation));

    let operationId = operation?.id || null;
    let createOperation = false;
    if (!operationId) {
      operationId = `dependency_${uuidv4()}`;
      createOperation = true;
      setOperation({
        id: operationId,
      } as any);
    }
    dispatchApiCall({
      method: createOperation ? 'post' : 'put',
      route: `/api/operations/${createOperation ? 'create' : `${operationId}/update`}`,
      body: { ...nextOperation, id: operationId },
      header: AuthStore.getAuthHeader(),
      onSuccess: (res) => {
        // console.log('operation response', res.data, JSON.stringify(res.data));
        const op = res.data as Operation;
        if (typeof op === 'object' && op?.id && questionnairePageVisible.current) {
          setOperation(op);
        }
      },
    } as ApiDispatchItem);

    // hide error states
    setShowErrorStates(false);

    // skip leads if user has no permissions
    if (currentPage === 3 && (skipProductSelection.current || !hasLeadPermission)) {
      goNext(2);
      logAction({
        type: 'SKIP_PRODUCT_SELECTION',
      } as OperationHistorySkipProductSelection);
      return;
    }

    goNext();

    // operation is done
    if (nextOperation.status === OperationStatus.DONE) {
      // reset questionnaire data
      loadStateFromOperation(null);

      // navigate to operation detail page
      history.push(`/customers/${nextOperation.customerId}/operations/${operation?.id}`);
    }
  };

  return (
    <>
      <Wizard items={wizardData} />

      <ContentWrapper>
        {
          currentPage === 0 &&
          <QuestionnaireCustomerSelection
            customers={props.customers?.filter((customer) => AuthStore.hasCustomerEditPermission(customer)) || []}
            selectedCustomerState={selectedCustomerState}
            onCreateCustomer={(customer) => {
              if ((props.customers || []).findIndex(({ customerNumber }) => customer.customerNumber?.toLowerCase() === customerNumber?.toLowerCase()) >= 0) {
                showNotificationBanner({
                  error: true,
                  renderContent: () => <>Der Kunde kann nicht angelegt werden, da bereits ein Kunde mit derselben GP-Nummer existiert.</>,
                });
                return false;
              }

              const dependencyId = `dependency_${uuidv4()}`;
              const nextCustomer = {
                ...customer,
                id: dependencyId,
              };

              // log create action
              logAction({
                type: 'ADD_CUSTOMER',
                customer: nextCustomer,
              } as OperationHistoryAddCustomer);

              // set customer state
              selectedCustomerState[1](nextCustomer);

              // dispatch create call
              dispatchApiCall({
                method: 'post',
                route: `/api/customers/create`,
                body: nextCustomer,
                header: AuthStore.getAuthHeader(),
                onSuccess: (res) => {
                  if (!questionnairePageVisible.current) {
                    return;
                  }
                  console.log('create customer response', res.data);
                  selectedCustomerState[1](res.data);
                  // update history customer id
                  setTmpOperation((tmp) => ({
                    ...tmp,
                    history: (tmp?.history || []).map((item) => {
                      if (item.type === 'ADD_CUSTOMER' && (item as OperationHistoryAddCustomer).customer?.id === dependencyId) {
                        return {
                          ...item,
                          customer: res.data,   // customer data from api
                        };
                      }
                      return item;
                    }),
                  }));
                },
              });

              return true;
            }}
            onUpdateCustomer={(customer) => {
              if ((props.customers || []).filter(({ customerNumber }) => customer?.customerNumber?.toLowerCase() === customerNumber?.toLowerCase()).some(({ id }) => customer?.id !== id)) {
                showNotificationBanner({
                  error: true,
                  renderContent: () => <>Der Kunde kann nicht geändert werden, da bereits ein Kunde mit derselben GP-Nummer existiert.</>,
                });
                return false;
              }

              const dependencyId = `dependency_${uuidv4()}`;
              const nextCustomer = {
                ...customer,
                id: customer.id || selectedCustomerState[0]?.id || dependencyId,
              };

              // log update action
              logAction({
                type: 'UPDATE_CUSTOMER',
                oldCustomer: selectedCustomerState[0],
                newCustomer: nextCustomer,
              } as OperationHistoryUpdateCustomer);

              // set customer state
              selectedCustomerState[1](nextCustomer);

              // dispatch update customer call
              dispatchApiCall({
                method: 'put',
                route: `/api/customers/${nextCustomer.id}`,
                body: nextCustomer,
                header: AuthStore.getAuthHeader(),
                onSuccess: (res) => {
                  if (!questionnairePageVisible.current) {
                    return;
                  }
                  console.log('update customer response', res.data);
                  selectedCustomerState[1](res.data);
                },
              });

              return true;
            }}
          />
        }
        {
          currentPage === 1 &&
          <QuestionnaireBaseQuestions
            answersState={baseQuestionsState}
            additionalQuestionsAnswersState={additionalQuestionsState}
            customer={customer}
            questionnaire={questionnaireBase}
            showErrorStates={showErrorStates}
            onValueChanged={(id, value) => {
              setBaseQuestionsAnswered((state) => [...state, id]);
              const question = questionnaireBase?.questionnaire?.questions?.find((question) => question.id === id);
              if (question) {
                logAction({
                  type: 'ANSWER_QUESTION',
                  question: {
                    id,
                    label: question.label,
                    type: question.type,
                    required: question.required,
                    answered: true,
                    value,
                  },
                } as OperationHistoryAnswerQuestion);
              }
            }}
            onValuePreselected={(id, value) => {
              const question = questionnaireBase?.questionnaire?.questions?.find((question) => question.id === id);
              if (question) {
                logAction({
                  type: 'PRESELECT_QUESTION',
                  question: {
                    id,
                    label: question.label,
                    type: question.type,
                    required: question.required,
                    answered: true,
                    value,
                  },
                } as OperationHistoryPreselectQuestion);
              }
            }}
            onAdditionalValueChanged={(addressKey, questionId, value, questionnaireId) => {
              setAdditionalQuestionsAnswered((state) => ({
                ...state,
                [addressKey]: [...((state || {})[addressKey] || []), questionId],
              }));
              const question = questionnaireBase?.questionnaire?.additionalQuestions?.find((question) => question.id === questionId);
              const additionalAddress = customer?.additionalAddresses?.find((address, index) => `${index}` === addressKey);
              if (question) {
                logAction({
                  type: 'ANSWER_QUESTION',
                  question: {
                    id: questionId,
                    label: question.label,
                    type: question.type,
                    required: question.required,
                    answered: true,
                    value,
                  },
                  ...(additionalAddress && { additionalAddress: getAddressDisplayName(additionalAddress) } || {}),
                } as OperationHistoryAnswerQuestion);
              }
            }}
            onAdditionalValuePreselected={(addressKey, questionId, value, questionnaireId) => {
              const question = questionnaireBase?.questionnaire?.additionalQuestions?.find((question) => question.id === questionId);
              const additionalAddress = customer?.additionalAddresses?.find((address, index) => `${index}` === addressKey);
              if (question) {
                logAction({
                  type: 'PRESELECT_QUESTION',
                  question: {
                    id: questionId,
                    label: question.label,
                    type: question.type,
                    required: question.required,
                    answered: true,
                    value,
                  },
                  ...(additionalAddress && { additionalAddress: getAddressDisplayName(additionalAddress) } || {}),
                } as OperationHistoryPreselectQuestion);
              }
            }}
          />
        }
        {
          currentPage === 2 &&
          <>
            <Headline>
            {
              (() => {
                const headline = 'Die passende Lösung für Sie!';

                if ([
                  'admin@adesso.de',
                  'tony.hensche@dew21.de',
                  'silke.hupertz@dew21.de',
                  'sstengel@datalab-crm.de',
                ].includes(AuthStore.getSession()?.email || '')) {
                  return `${headline} (${resultSetString})`;
                }

                return headline;
              })()
            }
            </Headline>
            {
              productsList?.length > 0 || productsListPerAddress?.reduce((res, item) => res + item?.productsList?.length, 0) > 0 ?
                <>
                  {
                    productsList?.length > 0 &&
                    <ProductCategoryList
                      activeSections={openProductCategories}
                      setActiveSections={setOpenProductCategories}
                      productsList={productsList}
                      onProductItemClick={(item) => {
                        setSelectedProduct(item);
                        goNext();
                        additionalAddressSelected.current = null;
                      }}
                    />
                  }
                  {
                    (productsListPerAddress?.length > 0 && customer?.type === 'HOUSING_INDUSTRY') &&
                    <AdditionalAddressesResultsWrapper>
                      {
                        productsListPerAddress?.filter(({ productsList }) => productsList.length > 0).map(({ productsList, ...address }, index) => (
                          <SimpleAccordion
                            title={getAddressDisplayName(address)}
                            collapsed={!openAccordions.includes(`${index}`)}
                            onClick={(collapsed) => {
                              const next = openAccordions.filter((item) => item !== `${index}`);
                              if (collapsed) {
                                next.push(`${index}`);
                              }
                              setOpenAccordions(next);
                            }}
                          >
                            <ProductCategoryList
                              additionalList={true}
                              activeSections={openProductCategoriesPerAddress[`${index}`] || []}
                              setActiveSections={(sections) => setOpenProductCategoriesPerAddress((state) => ({
                                ...state,
                                [`${index}`]: sections,
                              }))}
                              productsList={productsList}
                              onProductItemClick={(item) => {
                                setSelectedProduct(item);
                                goNext();

                                // save address info
                                additionalAddressSelected.current = getAddressDisplayName(address);
                              }}
                            />
                          </SimpleAccordion>
                        ))
                      }
                    </AdditionalAddressesResultsWrapper>
                  }
                </>
                :
                <NoDataPlaceholder>Es wurden leider keine passenden Produkte gefunden.</NoDataPlaceholder>
            }
          </>
        }
        {
          currentPage === 3 &&
          <ProductDetailPage
            product={selectedProduct}
            onBackButtonClick={onBackButtonClick}
            onSkipButtonClick={() => {
              skipProductSelection.current = true;
              onNextButtonClick();
            }}
            ctaButtons={[
              {
                onCTAButtonClick: () => {
                  skipProductSelection.current = false;
                  leadPageType.current = 'PRODUCT_INFO';
                  onNextButtonClick();
                },
                ctaButtonState: selectedProductAttachments?.find((attachment) => attachment.fileName?.startsWith('produktsteckbrief')) ? 'visible' : 'disabled',
                ctaButtonTitle: 'Produktsteckbrief verschicken',
              },
              hasContractPermission && {
                onCTAButtonClick: () => {
                  skipProductSelection.current = false;
                  leadPageType.current = 'CONTRACT';
                  onNextButtonClick();
                },
                ctaButtonState: selectedProductAttachments?.find((attachment) => attachment.fileName?.startsWith('vertrag_')) ? 'visible' : 'disabled',
                ctaButtonTitle: 'Vertrag abschließen',
              },
              hasSampleContractPermission && {
                onCTAButtonClick: () => {
                  skipProductSelection.current = false;
                  leadPageType.current = 'SAMPLE_CONTRACT';
                  onNextButtonClick();
                },
                ctaButtonState: selectedProductAttachments?.find((attachment) => attachment.fileName?.startsWith('mustervertrag_')) ? 'visible' : 'disabled',
                ctaButtonTitle: 'Mustervertrag verschicken',
              },
              hasLeadPermission && {
                onCTAButtonClick: () => {
                  skipProductSelection.current = false;
                  leadPageType.current = 'LEAD';
                  onNextButtonClick();
                },
                ctaButtonState: 'visible',
                ctaButtonTitle: 'Lead generieren',
              },
              !hasContractPermission && !hasSampleContractPermission && !hasLeadPermission && {
                onCTAButtonClick: () => {
                  skipProductSelection.current = false;
                  onNextButtonClick();
                },
                ctaButtonState: 'disabled',
              },
            ].filter(Boolean) as CTAButtonProps[]}
          />
        }
        {
          currentPage === 4 && (
            leadPageType.current === 'CONTRACT' && hasContractPermission ?
              <QuestionnaireContract
                attachments={selectedProductAttachments}
                operationId={operation?.id || ''}
                emailReceiver={customer?.email || ''}
                onDocumentAction={onDocumentActionListener}
              />
              : leadPageType.current === 'SAMPLE_CONTRACT' && hasSampleContractPermission ?
              <QuestionnaireSampleContract
                attachments={selectedProductAttachments}
                operationId={operation?.id || ''}
                emailReceiver={customer?.email || ''}
                onDocumentAction={onDocumentActionListener}
              />
              : leadPageType.current === 'LEAD' && hasLeadPermission ?
                <QuestionnaireLeadQuestions
                  answersState={leadQuestionsState}
                  customer={customer}
                  showErrorStates={showErrorStates}
                  allQuestionnaires={allQuestionnairesLeads}
                  selectedUserState={selectedUserLeadState}
                  onValueChanged={(id, value, questionnaireId) => {
                    setLeadQuestionsAnswered([...leadQuestionsAnswered, id]);
                    const questionnaireLeads = allQuestionnairesLeads?.find(({ id }) => id === questionnaireId);
                    const question = questionnaireLeads?.questionnaire?.questions?.find((question) => question.id === id);
                    if (question) {
                      logAction({
                        type: 'ANSWER_QUESTION',
                        question: {
                          id,
                          label: question.label,
                          type: question.type,
                          required: question.required,
                          answered: true,
                          value,
                        },
                      } as OperationHistoryAnswerQuestion);
                    }
                  }}
                  onValuePreselected={(id, value, questionnaireId) => {
                    const questionnaireLeads = allQuestionnairesLeads?.find(({ id }) => id === questionnaireId);
                    const question = questionnaireLeads?.questionnaire?.questions?.find((question) => question.id === id);
                    if (question) {
                      logAction({
                        type: 'PRESELECT_QUESTION',
                        question: {
                          id,
                          label: question.label,
                          type: question.type,
                          required: question.required,
                          answered: true,
                          value,
                        },
                      } as OperationHistoryPreselectQuestion);
                    }
                  }}
                />
                :
                leadPageType.current === 'PRODUCT_INFO' ?
                  <QuestionnaireProductInfo
                    attachments={selectedProductAttachments}
                    operationId={operation?.id || ''}
                    emailReceiver={customer?.email || ''}
                    onDocumentAction={onDocumentActionListener} />
                  : <></>
          )
        }
        {
          currentPage === 5 &&
          <QuestionnaireConcludingQuestions
            answersState={concludingQuestionsState}
            customer={customer}
            questionnaire={questionnaireConcludingQuestions}
            showErrorStates={showErrorStates}
            onValueChanged={(id, value) => {
              setConcludingQuestionsAnswered([...concludingQuestionsAnswered, id]);
              const question = questionnaireConcludingQuestions?.questionnaire?.questions?.find((question) => question.id === id);
              if (question) {
                logAction({
                  type: 'ANSWER_QUESTION',
                  question: {
                    id,
                    label: question.label,
                    type: question.type,
                    required: question.required,
                    answered: true,
                    value,
                  },
                } as OperationHistoryAnswerQuestion);
              }
            }}
            onValuePreselected={(id, value) => {
              const question = questionnaireConcludingQuestions?.questionnaire?.questions?.find((question) => question.id === id);
              if (question) {
                logAction({
                  type: 'PRESELECT_QUESTION',
                  question: {
                    id,
                    label: question.label,
                    type: question.type,
                    required: question.required,
                    answered: true,
                    value,
                  },
                } as OperationHistoryPreselectQuestion);
              }
            }}
          />
        }

        {
          currentPage > 0 &&
          <NotesPopup preselectedTitle={wizardData.length > currentPage && wizardData[currentPage].title} />
        }

        <ButtonWrapper>
          {
            isBackButtonVisible() &&
            <Button
              type={'primary'}
              disabled={!isBackButtonEnabled()}
              onClick={onBackButtonClick}
            >Zurück</Button>
          }

          {
            currentPage === 5 &&
            <Button
              type={'secondary'}
              disabled={!isNextButtonEnabled()}
              onClick={onBackToResultsButtonClick}
            >Zurück zur Lösungsansicht</Button>
          }

          <Button
            type={currentPage === 5 ? 'success' : 'secondary'}
            style={!isNextButtonVisible() && { display: 'none' }}
            disabled={!isNextButtonEnabled()}
            onClick={onNextButtonClick}
          >{currentPage === 5 ? 'Beratung abschließen' : 'Weiter'}</Button>
        </ButtonWrapper>

        {
          currentPage === 5 &&
          <>
            <ConcludingNotes textState={[concludingNotes, setConcludingNotes]} />
            <NotesSummary />
          </>
        }
      </ContentWrapper>
    </>
  );
};
